import { useLocation } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../utils/formatDate';
import {
  downloadFileS3,
  getDailyStateByTuple,
  updateStatus,
  getFilePreviewUrlS3,
} from '../../../../services/dailyServices';
import PdfViewer from '../../../../components/PdfViewer/PdfViewer';
import { getAllInternalStates } from '../../../../services/internalStatusServices';
import { getCurrentUser } from '../../../../services/authService';
import { getEffectiveSort, sortRowsByKey } from '../../../../utils/tableSort';
import useViewportHeight from 'utils/useViewportHeight';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { configMovementsI18n } from './i18n';
import usePermissionsCauses from 'permissions/modules/causes/usePermissionsCauses';
import { useSnackbar } from 'context/SnackbarContext';

const DEFAULT_SORT = {
  key: 'fechaDoc',
  direction: 'desc',
};

const CaseMovements = () => {
  const { language } = useLanguage();
  const configMovementsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configMovementsI18n });
  const { showSnackbar } = useSnackbar();
  const location = useLocation();

  const { DesignSystemComponents, caseData } = useOutletContext();
  const { IconButton, Badge, Dropdown, Table, Modal, SvgIcon } =
    DesignSystemComponents;
  const [documentsInfo, setDocumentsInfo] = useState([]);
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [isMovLoading, setIsMovLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [userId, setUserId] = useState(null);
  const [previewMov, setPreviewMov] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const { canViewDocument, canManageMovements } = usePermissionsCauses();

  const resolvedCaseData =
    caseData ||
    location.state?.caseData ||
    JSON.parse(localStorage.getItem('dailyCaseData'));

  useEffect(() => {
    configMovementsI18n.changeLanguage(configMovementsLanguage);
  }, [configMovementsLanguage]);

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    } else {
      console.warn('No se pudo obtener el usuario autenticado');
    }
  }, []);

  const fetchMovements = async () => {
    if (
      !userId ||
      !resolvedCaseData ||
      !resolvedCaseData.code ||
      !resolvedCaseData.civilCourtCode ||
      !resolvedCaseData.newCivilCourtCode ||
      !resolvedCaseData.businessAreaId
    ) {
      return;
    }

    setIsMovLoading(true);

    try {
      const resp = await getDailyStateByTuple(
        resolvedCaseData.code,
        resolvedCaseData.civilCourtCode,
        resolvedCaseData.newCivilCourtCode,
        resolvedCaseData.businessAreaId
      );

      const normalized = resp.flatMap((r) => {
        const daily = r.dailyState;
        const docs = r.documentos ?? [];

        if (docs.length > 0) {
          return docs.map((doc) => {
            const d = doc.documento;

            return {
              id: daily.id,
              caseId: resolvedCaseData.id,
              fechaDoc: daily.fecha_estado,
              tramite: d.tramite || '--',
              descripcionTramite:
                d.descripcionTramite ?? daily.observacion ?? '--',
              urlDocumento: d.documentoUrl || d.urlDocumento,
              tribunalId: r.tribunalId,
              stateId: r.internalStatusId,
              state: r.internalStatusName || 'Pendiente',
              badgeColor: r.internalStatusColor || 'warning',
              rolCausa: d.rolCausa || daily.rol,
              isHighPriority: doc.isHighPriority,
            };
          });
        }

        return [
          {
            id: daily.id,
            caseId: resolvedCaseData.id,
            fechaDoc: daily.fecha_estado,
            tramite: '--',
            descripcionTramite: daily.observacion ?? '--',
            urlDocumento: null,
            tribunalId: r.tribunalId,
            stateId: r.internalStatusId,
            state: r.internalStatusName || 'Pendiente',
            badgeColor: r.internalStatusColor || 'warning',
            rolCausa: daily.rol,
            isHighPriority: false,
          },
        ];
      });

      setDocumentsInfo(normalized);

      setPage(0);
    } catch (error) {
      console.error('Error al obtener movimientos:', error);
      setDocumentsInfo([]);
    } finally {
      setIsMovLoading(false);
    }
  };

  const fetchInternalStatus = async () => {
    try {
      const resp = await getAllInternalStates();

      const mapped = resp.map((s) => ({
        label: s.name,
        value: s.id,
        color: s.badgeColor,
      }));

      setStates(mapped);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!resolvedCaseData?.id || !resolvedCaseData?.businessAreaId || !userId)
      return;

    const fetchData = async () => {
      await fetchMovements();
      await fetchInternalStatus();
    };

    fetchData();
  }, [resolvedCaseData?.id, resolvedCaseData?.businessAreaId, userId]);

  const handleDownload = async (mov) => {
    try {
      await downloadFileS3(mov.urlDocumento, `${mov.tramite}.pdf`);
    } catch (err) {
      console.error('No se pudo descargar el archivo', err);
      showSnackbar(t('s3_download_error'), 'error');
    }
  };

  const handleOpenPreview = async (mov) => {
    if (!mov?.urlDocumento) return;

    try {
      setLoadingPreview(true);
      setPreviewMov(mov);
      const blobUrl = await getFilePreviewUrlS3(mov.urlDocumento);
      setPreviewUrl(blobUrl);
    } catch (err) {
      console.error('No se pudo cargar la vista previa del archivo', err);
      setPreviewUrl(null);
      showSnackbar(t('s3_preview_error'), 'error');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewMov(null);
  };

  const handleState = async (selected, item) => {
    try {
      setDocumentsInfo((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? {
                ...row,
                state: selected.label,
                stateId: selected.value,
                badgeColor: selected.color,
              }
            : row
        )
      );

      const payload = {
        dailyStateId: item.id,
        date: item.fechaDoc,
        caseId: item.caseId,
        code: item.rolCausa,
        newCivilCourtCode: item.tribunalId,
      };

      await updateStatus(payload, selected.value, userId);

      sessionStorage.removeItem('dailyStateCache');

      window.dispatchEvent(new Event('dailyStateUpdated'));
    } catch (error) {
      console.error(error);
    }
  };

  const effectiveSort = getEffectiveSort(
    sortKey,
    sortDirection,
    DEFAULT_SORT.key,
    DEFAULT_SORT.direction
  );

  const sortedDocuments = useMemo(
    () =>
      sortRowsByKey(documentsInfo, effectiveSort.key, effectiveSort.direction),
    [documentsInfo, effectiveSort.key, effectiveSort.direction]
  );

  const handleTableSortChange = ({ sortKey: key, direction }) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
    setPage(0);
  };

  const viewportHeight = useViewportHeight();

  let heightPercent = 60;

  if (viewportHeight < 600) {
    // Zoom 150%
    heightPercent = 78;
  } else if (viewportHeight < 700) {
    // Zoom 125%
    heightPercent = 87;
  } else if (viewportHeight < 800) {
    // Zoom 110%
    heightPercent = 90;
  } else if (viewportHeight < 900) {
    // Zoom 100%
    heightPercent = 92;
  } else if (viewportHeight < 1000) {
    // Zoom 90%
    heightPercent = 95;
  } else if (viewportHeight < 1100) {
    // Zoom 80%
    heightPercent = 97;
  }

  return (
    <div
      className='mt-3 case-movements-tab'
      style={{
        height: `${heightPercent}%`,
      }}
    >
      <div className='px-4'>
        {isMovLoading && (
          <p className='font-light m-2'>{t('configMovements_loading')}</p>
        )}

        {!isMovLoading && documentsInfo.length === 0 && (
          <p className='font-light text-center mt-4'>
            {t('configMovements_empty')}
          </p>
        )}
      </div>
      <div className='just-table px-4'>
        {!isMovLoading && documentsInfo.length > 0 && (
          <Table
            columns={[
              {
                accessor: 'fechaDoc',
                label: t('configMovements_col_date'),
                sort: true,
                render: (row) => (
                  <span className='font-light small'>
                    {row.fechaDoc ? formatDate(row.fechaDoc) : '--'}
                  </span>
                ),
              },
              {
                accessor: 'tramite',
                label: t('configMovements_col_process'),
                sort: true,
              },
              {
                accessor: 'descripcionTramite',
                label: t('configMovements_col_description'),
                sort: true,
              },
              {
                accessor: 'state',
                label: t('configMovements_col_status'),
                sort: true,
                render: (row) => (
                  <div className='d-flex align-items-center'>
                    <Badge text={row.state} color={row.badgeColor} size='sm' />
                    {row.isHighPriority && (
                      <SvgIcon
                        name='exclamationTriangle'
                        size='1rem'
                        className='ms-2'
                        fill='var(--error-500)'
                      />
                    )}
                  </div>
                ),
              },
              {
                accessor: 'urlDocumento',
                label: t('configMovements_col_document'),
                render: (row) =>
                  row.urlDocumento && (
                    <IconButton
                      label={t('configMovements_viewDocument')}
                      icon='fileEarmarkText'
                      size='md'
                      onClick={() => handleOpenPreview(row)}
                      disabled={!canViewDocument}
                    />
                  ),
              },
              {
                label: t('configMovements_col_actions'),
                render: (row) => (
                  <div className='d-flex gap-2'>
                    <Dropdown
                      ariaLabel={t('configMovements_editStatusAria')}
                      icon='threeDotsVertical'
                      onSelect={(selected) => handleState(selected, row)}
                      options={states}
                      variant='icon'
                      disabled={!canManageMovements}
                    />
                  </div>
                ),
              },
            ]}
            data={sortedDocuments}
            page={page}
            onPageChange={setPage}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '30', value: 30 },
            ]}
            initialPageSize={pageSize}
            paginationMode='client'
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleTableSortChange}
          />
        )}
      </div>
      {Modal && previewMov && (
        <Modal
          title={t('configMovements_preview_title')}
          show={!!previewMov}
          onClose={handleClosePreview}
          primaryBtnTxt={t('configMovements_preview_download')}
          primaryBtnAction={() => {
            if (previewMov) {
              handleDownload(previewMov);
            }
          }}
          secondaryBtnTxt={t('configMovements_preview_close')}
          secondaryBtnAction={handleClosePreview}
          ctaAligment='right'
          width='900px'
        >
          <div style={{ height: '70vh' }}>
            {loadingPreview && (
              <p className='caption font-light'>
                {t('configMovements_preview_loading')}
              </p>
            )}
            {!loadingPreview && previewUrl && (
              <PdfViewer pdf={previewUrl} className='h-100' />
            )}
            {!loadingPreview && !previewUrl && (
              <p className='caption font-light'>
                {t('configMovements_preview_error')}
              </p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CaseMovements;
